/**
 * Los cobros de una factura emitida — `OD-0040` (`ACCEPTED` 2026-10-17), plan
 * Business (feature `payments`).
 *
 * `D-04`: el cobro es un atributo de la factura, así que vive en su ficha, en
 * un bloque propio, y no en una sección del menú. Se apunta lo que te han
 * pagado —importe, fecha, forma de pago y una nota— y se ve lo que falta.
 *
 * ## Lo que esta pantalla NO decide
 *
 * - **Ninguna cuenta de dinero**: total a cobrar, cobrado y pendiente llegan de
 *   la API ya sumados (`D-08`, `URS §129`). El importe que se propone al
 *   apuntar un cobro es el pendiente tal cual lo manda la API.
 * - **Ni si el plan la incluye**: lo dice la API con un 402 (`D-09`). Entonces
 *   la tarjeta se ve con el aviso y el enlace a los planes, sin ocultarla.
 * - **Ni si se puede cobrar**: `blockedBy` llega resuelto y aquí sólo se
 *   escribe en llano.
 *
 * Un cobro no se edita ni se borra (`D-06`): se anula con un motivo, y sigue en
 * la lista tachado para que quede constancia.
 */
import { useCallback, useEffect, useState } from "react";

import { api, ESTADO_VALIDACION, METODOS_COBRO, type ErrorApi, type FichaCobros, type MetodoCobro } from "../api/cliente";
import { t } from "../i18n/es";
import { diaLegible, importeLegible, sinPuntosDeMiles } from "../ui/formato";
import { AreaTexto, Aviso, Boton, Campo, Selector, Tarjeta } from "../ui/primitivas";

/** El día de hoy en Madrid, AAAA-MM-DD: sólo para proponer la fecha; la valida la API. */
function hoy(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Madrid" }).format(new Date());
}

type Estado =
  | { readonly tipo: "cargando" }
  | { readonly tipo: "oculta" }
  | { readonly tipo: "sin-plan" }
  | { readonly tipo: "error" }
  | { readonly tipo: "datos"; readonly ficha: FichaCobros };

export function TarjetaCobros({
  facturaId,
  hayConexion,
  onCuenta,
  onSesionCaducada,
}: {
  readonly facturaId: string;
  readonly hayConexion: boolean;
  /**
   * `OD-0050-ENMIENDA-01` E-04: la ficha quiere saber si la factura está
   * cobrada entera, y si lo acaba de quedar por un cobro apuntado AQUÍ, para
   * proponer cerrar sus tareas. Esta tarjeta no sabe nada de tareas.
   */
  readonly onCuenta?: (cuenta: { readonly cobradaEntera: boolean; readonly trasApuntar: boolean }) => void;
  readonly onSesionCaducada: () => void;
}) {
  const [estado, setEstado] = useState<Estado>({ tipo: "cargando" });
  const [importe, setImporte] = useState("");
  const [fecha, setFecha] = useState(hoy);
  const [metodo, setMetodo] = useState<MetodoCobro>("TRANSFERENCIA");
  const [nota, setNota] = useState("");
  // La misma clave en los reintentos de UN apunte (D-10): un botón pulsado dos
  // veces con mala conexión no apunta dos cobros. Se renueva tras apuntarlo.
  const [clave, setClave] = useState(() => crypto.randomUUID());
  const [errores, setErrores] = useState<Readonly<Record<string, string>>>({});
  const [mensaje, setMensaje] = useState<{ readonly tono: "exito" | "aviso" | "error"; readonly texto: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [anulando, setAnulando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState("");

  const cargar = useCallback(async (trasApuntar = false): Promise<void> => {
    const r = await api.cobrosDeFactura(facturaId);
    if (r.ok) {
      setEstado({ tipo: "datos", ficha: r.datos });
      setImporte(r.datos.cuenta.pending);
      onCuenta?.({ cobradaEntera: r.datos.cuenta.blockedBy === "FULLY_PAID", trasApuntar });
      return;
    }
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === 402) return setEstado({ tipo: "sin-plan" });
    // Sin permiso para ver cobros: la tarjeta no se pinta y la ficha sigue a lo suyo.
    setEstado({ tipo: r.estado === 403 ? "oculta" : "error" });
  }, [facturaId, onCuenta, onSesionCaducada]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  function rechazo(r: { estado: number; error: ErrorApi | null }): void {
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === 403) return setMensaje({ tono: "aviso", texto: t.cobros.sinPermiso });
    if (r.estado === ESTADO_VALIDACION && r.error !== null) return setErrores(r.error.campos);
    if (r.estado === 409 && r.error?.motivo === "OVERPAYMENT") {
      const pendiente = r.error.detalles.pending;
      return setMensaje({ tono: "aviso", texto: t.cobros.sobrepago(importeLegible(typeof pendiente === "string" ? pendiente : "0,00")) });
    }
    if (r.estado === 409 && r.error?.motivo === "ALREADY_VOIDED") return setMensaje({ tono: "aviso", texto: t.cobros.yaAnulado });
    setMensaje({ tono: "error", texto: t.cobros.error });
  }

  async function registrar(): Promise<void> {
    setOcupado(true);
    setErrores({});
    setMensaje(null);
    const r = await api.registrarCobro(facturaId, { amount: sinPuntosDeMiles(importe), paidOn: fecha, method: metodo, note: nota.trim() === "" ? null : nota }, clave);
    setOcupado(false);
    if (!r.ok) return rechazo(r);
    setClave(crypto.randomUUID());
    setNota("");
    setMensaje({ tono: "exito", texto: t.cobros.registrado });
    await cargar(true);
  }

  async function anular(cobroId: string): Promise<void> {
    setOcupado(true);
    setErrores({});
    setMensaje(null);
    const r = await api.anularCobro(facturaId, cobroId, motivo);
    setOcupado(false);
    if (!r.ok) {
      if (r.estado === ESTADO_VALIDACION && r.error !== null) return setErrores({ motivo: r.error.campos.reason ?? "REQUIRED" });
      return rechazo(r);
    }
    setAnulando(null);
    setMotivo("");
    setMensaje({ tono: "exito", texto: t.cobros.cobroAnulado });
    await cargar();
  }

  const errorDe = (campo: string): string | null =>
    errores[campo] === undefined ? null : (t.cobros.errores[errores[campo]] ?? t.comun.errorGenerico);

  if (estado.tipo === "cargando" || estado.tipo === "oculta") return null;

  if (estado.tipo === "sin-plan") {
    return (
      <Tarjeta titulo={t.cobros.titulo}>
        <Aviso tono="info">{t.cobros.sinPlan}</Aviso>
        <a className="boton boton-secundario enlace-boton" href="/plan">
          {t.cobros.verPlanes}
        </a>
      </Tarjeta>
    );
  }

  if (estado.tipo === "error") {
    return (
      <Tarjeta titulo={t.cobros.titulo}>
        <Aviso tono="error">{t.cobros.error}</Aviso>
      </Tarjeta>
    );
  }

  const { cobros, cuenta } = estado.ficha;
  return (
    <Tarjeta titulo={t.cobros.titulo}>
      {cuenta.paymentStatus !== null ? (
        <p>
          <span className={`insignia insignia-pago-${cuenta.paymentStatus}`}>{t.cobros.estados[cuenta.paymentStatus]}</span>
        </p>
      ) : null}
      {cuenta.amountToCollect !== null && cuenta.blockedBy !== "RECTIFICATIVE" && cuenta.blockedBy !== "NOT_ISSUED" ? (
        <>
          <dl className="cifras">
            <div>
              <dt>{t.cobros.aCobrar}</dt>
              <dd>{importeLegible(cuenta.amountToCollect)} €</dd>
            </div>
            <div>
              <dt>{t.cobros.cobrado}</dt>
              <dd>{importeLegible(cuenta.collected)} €</dd>
            </div>
            <div>
              <dt>{t.cobros.pendiente}</dt>
              <dd>{importeLegible(cuenta.pending)} €</dd>
            </div>
          </dl>
          <p className="ayuda-neutra">{t.cobros.ayudaACobrar}</p>
        </>
      ) : null}

      {cuenta.blockedBy !== null ? (
        <Aviso tono={cuenta.blockedBy === "FULLY_PAID" ? "exito" : "info"}>{t.cobros.bloqueos[cuenta.blockedBy]}</Aviso>
      ) : null}
      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}

      {cobros.length > 0 ? (
        <>
          <h3>{t.cobros.lista}</h3>
          <ul className="lista-filas">
            {cobros.map((c) => (
              <li key={c.id} className="fila">
                <span className="fila-texto">
                  <span className={c.status === "VOIDED" ? "fila-titulo cobro-anulado" : "fila-titulo"}>{importeLegible(c.amount)} €</span>
                  <span className="ayuda-neutra">
                    {diaLegible(c.paidOn)} · {t.cobros.metodos[c.method]}
                    {c.note !== null ? ` · ${c.note}` : ""}
                  </span>
                  {c.status === "VOIDED" ? <span className="ayuda-neutra">{t.cobros.anuladoPor(c.voidReason ?? t.cobros.anulado)}</span> : null}
                </span>
                {c.status === "POSTED" && anulando !== c.id ? (
                  <Boton variante="terciario" onClick={() => setAnulando(c.id)} deshabilitado={ocupado || !hayConexion}>
                    {t.cobros.anular}
                  </Boton>
                ) : null}
              </li>
            ))}
          </ul>
          {anulando !== null ? (
            <div>
              <Campo id="cobro-motivo" etiqueta={t.cobros.motivoAnular} valor={motivo} onChange={setMotivo} error={errorDe("motivo")} />
              <div className="acciones">
                <Boton variante="peligro" onClick={() => void anular(anulando)} deshabilitado={ocupado || !hayConexion}>
                  {ocupado ? t.cobros.anulando : t.cobros.confirmarAnular}
                </Boton>
                <Boton variante="secundario" onClick={() => setAnulando(null)}>
                  {t.cobros.cancelar}
                </Boton>
              </div>
            </div>
          ) : null}
        </>
      ) : cuenta.blockedBy === null ? (
        <p className="ayuda-neutra">{t.cobros.sinCobros}</p>
      ) : null}

      {cuenta.blockedBy === null ? (
        <>
          <h3>{t.cobros.nuevo}</h3>
          <Campo
            id="cobro-importe"
            etiqueta={t.cobros.importe}
            valor={importe}
            onChange={setImporte}
            inputMode="text"
            ayuda={t.cobros.ayudaImporte}
            error={errorDe("amount")}
          />
          <Campo id="cobro-fecha" etiqueta={t.cobros.fechaCobro} valor={fecha} onChange={setFecha} tipo="date" error={errorDe("paidOn")} />
          <Selector
            id="cobro-metodo"
            etiqueta={t.cobros.metodo}
            valor={metodo}
            opciones={METODOS_COBRO.map((m) => ({ valor: m, texto: t.cobros.metodos[m] ?? m }))}
            onChange={setMetodo}
            error={errorDe("method")}
          />
          <AreaTexto id="cobro-nota" etiqueta={t.cobros.nota} valor={nota} onChange={setNota} ayuda={t.cobros.ayudaNota} error={errorDe("note")} requerido={false} />
          <Boton onClick={() => void registrar()} deshabilitado={ocupado || !hayConexion}>
            {ocupado ? t.cobros.registrando : t.cobros.registrar}
          </Boton>
        </>
      ) : null}
    </Tarjeta>
  );
}
